// Component copy to ScheduledTournamentConfirmationScreen.tsx on scheduled-tournament-confirmation
import { useCallback, useEffect } from 'react'
import { Dimensions, StyleSheet, Clipboard, BackHandler } from 'react-native'
import { observer } from 'mobx-react-lite'
import { SafeAreaView } from 'react-native-safe-area-context'
import { DS } from '../../design-system'
import { ScheduledGameCard } from '../../components/ScheduledGameCard'
import { TouchableOpacity } from 'react-native-gesture-handler'
import ShareGameQR from '../../components/ShareGameQR/ShareGameQR'
import { useStore } from '../../models'
import { events } from '../../models/analytics/analytics-events'
import { formatGameScheduledAt } from '../../utils'
import { getRouteToLastVisitedTab } from '../../navigation'
import { WebWrapper } from '@/components/WebWrapper'
import { useLocalSearchParams, useRouter } from 'expo-router'

export const ScheduledGameConfirmationScreen = observer(() => {
  const router = useRouter()
  const { shouldManage } = useLocalSearchParams<{ shouldManage: string }>()

  const {
    share: { promptShare },
    session: { lastVisitedTab },
    game: { formattedGameScheduleFullResponse, qrUrl, setToast },
    analytics
  } = useStore()

  const onClose = () => {
    router.replace(getRouteToLastVisitedTab(lastVisitedTab))
  }

  const openQrLightBox = () => {
    router.navigate('/lobby/qr-code')
  }

  const onShareGame = () => {
    const date = formatGameScheduledAt(formattedGameScheduleFullResponse.scheduledAt)
    const message = `Challenge accepted? Join my Poker Power game on ${date}—let's see who wins!`

    const title = formattedGameScheduleFullResponse?.name
    promptShare(message, title, qrUrl)

    analytics.setEvent(events.game.scheduled_game_shared, {
      game_name: formattedGameScheduleFullResponse?.name
    })
  }

  const goToEditScheduleScreen = () => {
    router.navigate('/game/create/tournament?isEdit=true')
  }

  const onCopyGameCode = () => {
    Clipboard.setString(formattedGameScheduleFullResponse?.hash ?? '')

    setToast({
      kind: 'Game Code',
      message: 'Code copied.'
    })
  }

  useEffect(() => {
    const listener = BackHandler.addEventListener('hardwareBackPress', () => {
      return true
    })

    return () => {
      listener.remove()
    }
  }, [])

  return (
    <SafeAreaView style={styles.container}>
      <WebWrapper>
        <DS.Frame row justifyContent="space-between" alignItems="center" style={styles.headerContainer}>
          <TouchableOpacity onPress={onClose}>
            <DS.Icon name="Close" size={24} onPress={onClose} color={'Global/White'} />
          </TouchableOpacity>
          {formattedGameScheduleFullResponse.isHost && (
            <DS.Text variant="Body Medium/Medium" color={'Global/White'} onPress={goToEditScheduleScreen}>
              Edit Game
            </DS.Text>
          )}
        </DS.Frame>

        <DS.Frame row style={styles.titleContainer} gap="12">
          {!shouldManage ? (
            <>
              <DS.Icon name="Check--enclosed" size={32} color="Brand/Blue" />
              <DS.Text variant="Heading 1/Medium" color="Global/White">
                Game Created
              </DS.Text>
            </>
          ) : (
            <DS.Text variant="Heading 1/Medium" color="Brand/Blue" style={{ textTransform: 'capitalize' }}>
              {formattedGameScheduleFullResponse.gameType}
            </DS.Text>
          )}
        </DS.Frame>

        <ScheduledGameCard
          {...formattedGameScheduleFullResponse}
          onCopyGameCode={onCopyGameCode}
          onShareGame={onShareGame}
        />

        <DS.Frame style={styles.shareGameQr}>
          <ShareGameQR onPress={openQrLightBox} />
        </DS.Frame>
      </WebWrapper>
    </SafeAreaView>
  )
})

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: DS.palette['Brand/Purple'],
    padding: DS.spacing['24'],
    paddingBottom: 0
  },
  headerContainer: {
    width: '100%'
  },
  titleContainer: {
    marginTop: DS.spacing['32'],
    marginBottom: DS.spacing['24']
  },
  shareGameQr: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0
  }
})
